import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFetchRecipientsCount } from "../hooks/UseFetchRecipientsCount";
import { LoadingOutlined } from "@ant-design/icons";
import { FormInstance } from "antd";
import { NamePath } from "antd/es/form/interface";

type RecipientsCounterProps = {
  request?: any;
  form: FormInstance;
  count: number;
  setCount: React.Dispatch<React.SetStateAction<number>>;
};

export const RecipientsCounter = ({ request, form, count, setCount }: RecipientsCounterProps) => {
  const { isLoading, fetchRecipientsCount } = useFetchRecipientsCount();

  const timeoutRef = useRef<number>();

  const abortRef = useRef<AbortController>();

  const lastAppliedSignature = useRef("[]");

  const getRequestSignature = useCallback((request: any) => JSON.stringify(request), []);

  const fieldsForValidate: NamePath[] = useMemo(() => {
    return [
      ["criteria", "statusesFilter"],
      ["criteria", "recipientTypesFilter"],
      ["criteria", "contractNumbersFilter"],
      ["criteria", "nextPaymentDatesFilter"],
      ["criteria", "impoundTypesFilter"],
      ["criteria", "paymentRestrictionsFilter"]
    ];
  }, []);

  useEffect(() => {
    if (!request) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(async () => {
      const signature = getRequestSignature(request);

      if (signature === lastAppliedSignature.current) {
        return;
      }

      abortRef.current?.abort();
      abortRef.current = new AbortController();

      try {
        await form.validateFields(fieldsForValidate);
        const result = await fetchRecipientsCount(request.criteria, abortRef.current.signal);
        if (abortRef.current.signal.aborted) {
          return;
        }
        lastAppliedSignature.current = signature;
        setCount(result ?? 0);
      } catch (error) {}
    }, 1000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [request, form, fetchRecipientsCount, getRequestSignature, fieldsForValidate, setCount]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return isLoading ? (
    <LoadingOutlined />
  ) : (
    <span>
      {count === 0 ? (
        <>
          <b>{count}</b> matching recipients. Change recipient criteria
        </>
      ) : (
        <>
          <b className="mr-1">{count}</b> matching recipients
        </>
      )}
    </span>
  );
};
